// Single source of truth for the editable text on the public legal / info pages
// (Privacy, Terms, Cancellation & Refund, Contact). The pages render these defaults
// deep-merged with any admin overrides fetched from /public/legal-pages, so the admin
// can edit the centralized copy while the page design stays in code. An empty override
// (or a failed fetch) renders these defaults unchanged — safe and non-breaking.
import { useEffect, useState } from 'react';
import { api } from './apiClient.js';

// --- Built-in defaults (mirror each page's original COPY object) ---
export const LEGAL_DEFAULTS = {
  privacy: {
    en: {
      title: 'Privacy Policy',
      subtitle: 'Learn how DayCal protects your personal information and data.',
      badge: 'Privacy You Can Trust',
      lastUpdated: 'August 21, 2026',
      toc: [
        { id: 'overview', title: '1. Overview & Commitment' },
        { id: 'collection', title: '2. Data We Collect' },
        { id: 'usage', title: '3. How We Use Data' },
        { id: 'security', title: '4. Storage & Encryption' },
        { id: 'rights', title: '5. Your Rights & Deletion' },
        { id: 'contact', title: '6. Contact Us' },
      ],
      sidebar: {
        indexTitle: 'Policy Index',
        helpText: 'Questions about your data?',
        helpLink: 'Reach Security Team',
      },
      sections: {
        overview: {
          heading: '1. Overview & Commitment',
          p1: 'DayCal ("we", "our", or "us") provides a modern appointment scheduling and calendar management platform. We respect your personal privacy and are committed to safeguarding all data transmitted through our web application.',
          p2: 'This Privacy Policy outlines how DayCal collects, uses, stores, and protects your information when you access our website or use our services.',
        },
        collection: {
          heading: '2. Data We Collect',
          intro: 'We only collect data necessary to fulfill scheduling requests and manage appointment availabilities:',
          items: [
            '<strong>Account Information:</strong> Name, email address, password hash, and avatar picture when you register or sign in.',
            '<strong>Scheduling Preferences:</strong> Working hours, timezone, custom booking form fields, event type titles, and pricing options.',
            '<strong>Attendee Data:</strong> Name, email address, and form responses submitted by clients booking appointments with you.',
          ],
        },
        usage: {
          heading: '3. How We Use Data',
          intro: 'We use collected information solely for operational functionality:',
          items: [
            'Generating custom public booking URLs (e.g. <code>/u/username</code>).',
            'Sending email confirmations and reminders for scheduled meetings.',
            'Processing payment transactions via Stripe or Razorpay when configured by hosts.',
          ],
        },
        security: {
          heading: '4. Storage & Security',
          body: 'All data transfers are protected using industry-standard TLS/SSL encryption in transit. Sensitive tokens and secrets are encrypted at rest using AES-256 encryption. We implement strict access controls and regular audit logs.',
        },
        rights: {
          heading: '5. Your Rights & Data Revocation',
          p1: 'You retain full ownership of your data. You can review and update your profile and settings at any time from your DayCal dashboard.',
          p2: 'You can request complete account and data deletion by contacting our privacy officer.',
        },
        contact: {
          heading: '6. Contact Us',
          intro: 'If you have questions regarding this Privacy Policy or DayCal\'s data practices, contact us at:',
          pocLabel: 'Grievance & Privacy Officer:',
          pocValue: 'Dibyendu Kumar Khaskel',
          emailLabel: 'Email:',
          emailValue: 'support@daycal.com',
          phoneLabel: 'Direct Helpline:',
          phoneValue: '+91 6294629735',
          addressLabel: 'Operating Address:',
          addressValue: 'Helencha Road, Duttapulia, Baikola, West Bengal 743270, India',
          portalLabel: 'Support Portal:',
          portalValue: 'DayCal Contact Us Page',
        },
      },
    },
    bn: {
      title: 'প্রাইভেসি পলিসি',
      subtitle: 'DayCal কীভাবে আপনার ব্যক্তিগত তথ্য ও ডেটা রক্ষা করে তা জানুন।',
      badge: 'আপনার গোপনীয়তা সুরক্ষিত',
      lastUpdated: '২১ আগস্ট, ২০২৬',
      toc: [
        { id: 'overview', title: '১. ওভারভিউ এবং অঙ্গীকার' },
        { id: 'collection', title: '২. আমরা যে ডেটা সংগ্রহ করি' },
        { id: 'usage', title: '৩. ডেটা ব্যবহারের নিয়ম' },
        { id: 'security', title: '৪. সিকিউরিটি ও এনক্রিপশন' },
        { id: 'rights', title: '৫. আপনার অধিকার ও ডেটা মোছা' },
        { id: 'contact', title: '৬. যোগাযোগ' },
      ],
      sidebar: {
        indexTitle: 'Policy Index',
        helpText: 'Questions about your data?',
        helpLink: 'Reach Security Team',
      },
      sections: {
        overview: {
          heading: '1. Overview & Commitment',
          p1: 'DayCal ("we", "our", or "us") provides a modern appointment scheduling and calendar management platform. We respect your personal privacy and are committed to safeguarding all data transmitted through our web application.',
          p2: 'This Privacy Policy outlines how DayCal collects, uses, stores, and protects your information when you access our website or use our services.',
        },
        collection: {
          heading: '2. Data We Collect',
          intro: 'We only collect data necessary to fulfill scheduling requests and manage appointment availabilities:',
          items: [
            '<strong>Account Information:</strong> Name, email address, password hash, and avatar picture when you register or sign in.',
            '<strong>Scheduling Preferences:</strong> Working hours, timezone, custom booking form fields, event type titles, and pricing options.',
            '<strong>Attendee Data:</strong> Name, email address, and form responses submitted by clients booking appointments with you.',
          ],
        },
        usage: {
          heading: '3. How We Use Data',
          intro: 'We use collected information solely for operational functionality:',
          items: [
            'Generating custom public booking URLs (e.g. <code>/u/username</code>).',
            'Sending email confirmations and reminders for scheduled meetings.',
            'Processing payment transactions via Stripe or Razorpay when configured by hosts.',
          ],
        },
        security: {
          heading: '4. Storage & Security',
          body: 'All data transfers are protected using industry-standard TLS/SSL encryption in transit. Sensitive tokens and secrets are encrypted at rest using AES-256 encryption. We implement strict access controls and regular audit logs.',
        },
        rights: {
          heading: '5. Your Rights & Data Revocation',
          p1: 'You retain full ownership of your data. You can review and update your profile and settings at any time from your DayCal dashboard.',
          p2: 'You can request complete account and data deletion by contacting our privacy officer.',
        },
        contact: {
          heading: '6. Contact Us',
          intro: 'If you have questions regarding this Privacy Policy or DayCal\'s data practices, contact us at:',
          pocLabel: 'Grievance & Privacy Officer:',
          pocValue: 'Dibyendu Kumar Khaskel',
          emailLabel: 'Email:',
          emailValue: 'support@daycal.com',
          phoneLabel: 'Direct Helpline:',
          phoneValue: '+91 6294629735',
          addressLabel: 'Operating Address:',
          addressValue: 'Helencha Road, Duttapulia, Baikola, West Bengal 743270, India',
          portalLabel: 'Support Portal:',
          portalValue: 'DayCal Contact Us Page',
        },
      },
    },
  },

  terms: {
    en: {
      title: 'Terms of Service',
      subtitle: 'Please review the agreement governing your use of DayCal scheduling tools and calendar integrations.',
      badge: 'Standard Legal Terms',
      lastUpdated: 'August 21, 2026',
      toc: [
        { id: 'acceptance', title: '1. Acceptance of Terms' },
        { id: 'service', title: '2. Service Overview' },
        { id: 'google-integrations', title: '3. Payments & Third-Party Services' },
        { id: 'account', title: '4. Accounts & Responsibilities' },
        { id: 'acceptable-use', title: '5. Acceptable Use Policy' },
        { id: 'payments', title: '6. Subscriptions & Payments' },
        { id: 'disclaimer', title: '7. Limitation of Liability' },
        { id: 'termination', title: '8. Account Termination' },
        { id: 'contact', title: '9. Legal Contact' },
      ],
      sections: {
        acceptance: {
          heading: '1. Acceptance of Terms',
          body: 'By accessing, registering for, or using DayCal ("Service"), you acknowledge that you have read, understood, and agreed to be legally bound by these Terms of Service and our Privacy Policy.',
        },
        service: {
          heading: '2. Service Overview',
          body: 'DayCal provides an automated appointment scheduling platform allowing hosts to share booking availability, collect custom form information, process payments, and sync calendar events across integrated providers.',
        },
        googleIntegrations: {
          heading: '3. Payments & Third-Party Services',
          body: 'DayCal integrates with trusted third-party payment processors, including Stripe and Razorpay, to handle paid bookings.',
          notes: [
            '• Use of these payment services is subject to the respective provider\'s Terms of Service and Privacy Policy.',
            '• DayCal processes your data strictly in accordance with our <a href="/privacy" class="text-brand-600 font-semibold underline">Privacy Policy</a>.',
          ],
        },
        account: {
          heading: '4. Accounts & Responsibilities',
          body: 'You are responsible for maintaining the confidentiality of your account credentials and for all activities conducted under your account. You must immediately report any unauthorized access.',
        },
        acceptableUse: {
          heading: '5. Acceptable Use Policy',
          body: 'You agree not to use DayCal to send unsolicited spam, conduct fraudulent scheduling, distribute malware, or breach any applicable local or international laws.',
        },
        payments: {
          heading: '6. Subscriptions & Payment Processing',
          body: 'Paid tiers are billed according to specified billing cycles. Payment processing for host fees or client bookings is handled by Stripe or Razorpay under their respective terms. All cancellations, money-back guarantees, and refund requests are governed by our <a href="/refund" class="text-brand-600 font-semibold underline">Cancellation & Refund Policy</a>.',
        },
        disclaimer: {
          heading: '7. Limitation of Liability',
          body: 'To the maximum extent permitted by law, DayCal shall not be liable for indirect, incidental, or consequential damages arising from service downtime or third-party calendar API outages.',
        },
        termination: {
          heading: '8. Account Termination',
          body: 'We reserve the right to suspend or terminate accounts that violate these Terms or pose security risks to our infrastructure.',
        },
        contact: {
          heading: '9. Legal Contact',
          body: 'For questions regarding these Terms or legal compliance, please contact our legal representative:',
          pocLabel: 'Point of Contact:',
          pocValue: 'Dibyendu Kumar Khaskel',
          emailLabel: 'Support & Legal Email:',
          emailValue: 'support@daycal.com',
          phoneLabel: 'Direct Helpline:',
          phoneValue: '+91 6294629735',
          addressLabel: 'Operating Address:',
          addressValue: 'Helencha Road, Duttapulia, Baikola, West Bengal 743270, India',
          portalLabel: 'Helpdesk Portal:',
          portalValue: 'DayCal Contact Us Page',
        },
      },
    },
    bn: {
      title: 'সেবার শর্তাবলী',
      subtitle: 'DayCal শিডিউলিং টুলস এবং ক্যালেন্ডার ইন্টিগ্রেশন ব্যবহারের চুক্তি পর্যালোচনা করুন।',
      badge: 'আইনি শর্তাবলী',
      lastUpdated: '২১ আগস্ট, ২০২৬',
      toc: [
        { id: 'acceptance', title: '১. শর্তাবলীর সম্মতি' },
        { id: 'service', title: '২. সার্ভিসের বিস্তারিত' },
        { id: 'google-integrations', title: '৩. পেমেন্ট ও তৃতীয় পক্ষের পরিষেবা' },
        { id: 'account', title: '৪. অ্যাকাউন্ট ও দায়িত্ব' },
        { id: 'acceptable-use', title: '৫. ব্যবহার নীতি' },
        { id: 'payments', title: '৬. সাবস্ক্রিপশন ও পেমেন্ট' },
        { id: 'disclaimer', title: '৭. দায়বদ্ধতার সীমাবদ্ধতা' },
        { id: 'termination', title: '৮. অ্যাকাউন্ট বাতিলকরণ' },
        { id: 'contact', title: '৯. লিগ্যাল যোগাযোগ' },
      ],
      sections: {
        acceptance: {
          heading: '1. Acceptance of Terms',
          body: 'By accessing, registering for, or using DayCal ("Service"), you acknowledge that you have read, understood, and agreed to be legally bound by these Terms of Service and our Privacy Policy.',
        },
        service: {
          heading: '2. Service Overview',
          body: 'DayCal provides an automated appointment scheduling platform allowing hosts to share booking availability, collect custom form information, process payments, and sync calendar events across integrated providers.',
        },
        googleIntegrations: {
          heading: '3. Payments & Third-Party Services',
          body: 'DayCal integrates with trusted third-party payment processors, including Stripe and Razorpay, to handle paid bookings.',
          notes: [
            '• Use of these payment services is subject to the respective provider\'s Terms of Service and Privacy Policy.',
            '• DayCal processes your data strictly in accordance with our <a href="/privacy" class="text-brand-600 font-semibold underline">Privacy Policy</a>.',
          ],
        },
        account: {
          heading: '4. Accounts & Responsibilities',
          body: 'You are responsible for maintaining the confidentiality of your account credentials and for all activities conducted under your account. You must immediately report any unauthorized access.',
        },
        acceptableUse: {
          heading: '5. Acceptable Use Policy',
          body: 'You agree not to use DayCal to send unsolicited spam, conduct fraudulent scheduling, distribute malware, or breach any applicable local or international laws.',
        },
        payments: {
          heading: '6. Subscriptions & Payment Processing',
          body: 'Paid tiers are billed according to specified billing cycles. Payment processing for host fees or client bookings is handled by Stripe or Razorpay under their respective terms. All cancellations, money-back guarantees, and refund requests are governed by our <a href="/refund" class="text-brand-600 font-semibold underline">Cancellation & Refund Policy</a>.',
        },
        disclaimer: {
          heading: '7. Limitation of Liability',
          body: 'To the maximum extent permitted by law, DayCal shall not be liable for indirect, incidental, or consequential damages arising from service downtime or third-party calendar API outages.',
        },
        termination: {
          heading: '8. Account Termination',
          body: 'We reserve the right to suspend or terminate accounts that violate these Terms or pose security risks to our infrastructure.',
        },
        contact: {
          heading: '9. Legal Contact',
          body: 'For questions regarding these Terms or legal compliance, please contact our legal representative:',
          pocLabel: 'Point of Contact:',
          pocValue: 'Dibyendu Kumar Khaskel',
          emailLabel: 'Support & Legal Email:',
          emailValue: 'support@daycal.com',
          phoneLabel: 'Direct Helpline:',
          phoneValue: '+91 6294629735',
          addressLabel: 'Operating Address:',
          addressValue: 'Helencha Road, Duttapulia, Baikola, West Bengal 743270, India',
          portalLabel: 'Helpdesk Portal:',
          portalValue: 'DayCal Contact Us Page',
        },
      },
    },
  },

  refund: {
    en: {
      title: 'Cancellation & Refund Policy',
      subtitle: 'Transparent, fair, and compliant guidelines for DayCal subscriptions and paid appointment scheduling.',
      badge: 'Payment Gateway Verified Compliance',
      lastUpdated: 'August 21, 2026',
      toc: [
        { id: 'overview', title: '1. Overview & Policy Scope' },
        { id: 'subscription-refunds', title: '2. DayCal SaaS Subscriptions' },
        { id: 'booking-cancellations', title: '3. Host Appointment Cancellations' },
        { id: 'eligibility', title: '4. Refund Eligibility & Exceptions' },
        { id: 'timelines', title: '5. Mode of Refund & Timelines (5–7 Days)' },
        { id: 'disputes', title: '6. Chargebacks & Dispute Resolution' },
        { id: 'contact', title: '7. Billing & Grievance Contact' },
      ],
      facts: {
        card1: { label: 'Turnaround Time', value: '5 – 7 Business Days', sub: 'Credited to original source' },
        card2: { label: 'Guarantee', value: '14-Day SaaS Money-Back', sub: 'For first-time plan upgrades' },
        card3: { label: 'Refund Method', value: 'Original Payment Mode', sub: 'UPI, Card, or Net Banking' },
        card4: { label: 'Billing Support', value: 'billing@daycal.com', sub: '24–48h SLA response time' },
      },
      sidebar: {
        indexTitle: 'Policy Contents',
        helpText: 'Need immediate refund or billing assistance?',
        helpLink: 'Contact Billing Team',
      },
      sections: {
        overview: {
          heading: '1. Overview & Policy Scope',
          p1: 'This Cancellation & Refund Policy sets out the terms under which refunds and cancellations are processed for services provided by <strong>DayCal Platform Inc.</strong> ("DayCal", "we", "our", or "us").',
          p2: 'DayCal operates in two primary payment contexts:',
          boxATitle: 'A. Platform SaaS Plans',
          boxABody: 'Subscriptions paid directly to DayCal by hosts for software tools, advanced calendar sync, custom domains, and agency team access.',
          boxBTitle: 'B. Paid Appointment Bookings',
          boxBBody: 'Consultation fees paid by attendees to hosts via integrated payment gateways (Razorpay, Stripe) for booking time slots.',
        },
        subscriptionRefunds: {
          heading: '2. DayCal SaaS Subscriptions',
          block1Title: '14-Day Money-Back Guarantee',
          block1Body: 'If you upgrade to a paid DayCal plan (Pro, Agency, or Enterprise) for the first time and find that the platform does not meet your scheduling needs, you may request a <strong>100% full refund within 14 days</strong> of your initial purchase date. No questions asked.',
          block2Title: 'Self-Serve Subscription Cancellation',
          block2Body: 'You can cancel your recurring DayCal subscription at any time with a single click from your <strong>Dashboard &gt; Settings &gt; Billing</strong>. Upon cancellation:',
          block2Items: [
            'Your account remains active with full premium benefits until the conclusion of the currently paid billing period.',
            'No further automatic renewals or billing charges will occur after the expiration date.',
            'Your bookings, custom links, and settings will gracefully downgrade to the Free Tier without data loss.',
          ],
          block3Title: 'Duplicate or Erroneous Charges',
          block3Body: 'In the event of accidental duplicate payments, currency conversion errors, or billing system anomalies, our automated detection or manual support will issue an immediate 100% reversal.',
        },
        bookingCancellations: {
          heading: '3. Host Appointment Cancellations & Rescheduling',
          intro: 'When attendees book paid appointments with DayCal hosts, the following cancellation and refund policies apply:',
          card1Title: 'Advance Notice Cancellations (24+ Hours)',
          card1Body: 'Attendees who cancel a scheduled appointment at least <strong>24 hours prior</strong> to the scheduled meeting start time are eligible for a <strong>100% full refund</strong> or a free reschedule to another available host slot.',
          card2Title: 'Host-Initiated Cancellations',
          card2Body: 'If a host cancels an appointment or is unable to attend a scheduled session, the attendee is guaranteed an <strong>automatic 100% full refund</strong> or prioritized rescheduling.',
          card3Title: 'Rescheduling Flexibility',
          card3Body: 'Attendees can reschedule their booking at zero penalty directly through their booking confirmation link up until the host\'s configured reschedule deadline.',
        },
        eligibility: {
          heading: '4. Refund Eligibility & Non-Refundable Scenarios',
          eligibleTitle: 'Eligible for Full Refund',
          eligibleItems: [
            '• First-time subscription refund requests within 14 days.',
            '• Technical failure on DayCal preventing video link generation or calendar sync.',
            '• Duplicate payment charges caused by network drops.',
            '• Appointments cancelled by host or with >=24h attendee notice.',
          ],
          nonRefundableTitle: 'Non-Refundable Scenarios',
          nonRefundableItems: [
            '• Client no-shows without any prior cancellation notice.',
            '• Successfully completed consultations and meetings.',
            '• Subscription renewal requests made after the 14-day window.',
            '• Consumed third-party add-ons or custom domain setup fees.',
          ],
        },
        timelines: {
          heading: '5. Mode of Refund & Turnaround Timelines',
          subheading: 'Official Payment Gateway Settlement SLA',
          intro: 'To comply with standard international payment gateway regulations (including RBI guidelines, Visa/Mastercard rules, Razorpay, and Stripe):',
          items: [
            '<strong class="text-slate-900">Mode of Refund:</strong> All approved refunds are credited <strong>strictly back to the original payment source</strong> (Credit Card, Debit Card, UPI ID, Net Banking account, or Digital Wallet) used to make the payment. No cash refunds or untraceable third-party transfers are permitted.',
            '<strong class="text-slate-900">Processing SLA:</strong> DayCal initiates approved refund requests within <strong>24 to 48 business hours</strong>.',
            '<strong class="text-slate-900">Bank Settlement Timeline:</strong> Once initiated, the refunded funds typically reflect in the customer\'s bank account or card balance within <strong>5 to 7 business days</strong> (depending on the issuing bank\'s clearing cycle).',
            '<strong class="text-slate-900">Refund Reference Number (ARN):</strong> You will receive an automated email receipt containing the unique Gateway Refund Reference / Acquirer Reference Number (ARN) for bank tracking.',
          ],
        },
        disputes: {
          heading: '6. Chargebacks & Dispute Resolution',
          p1: 'We strongly encourage customers to contact DayCal Billing Support prior to initiating a bank chargeback or payment gateway dispute. Our dedicated support desk resolves 99% of billing inquiries within <strong>24 to 48 hours</strong>.',
          p2: 'If a chargeback is initiated, DayCal provides full transaction audit logs, booking confirmation emails, and delivery status to the payment processor in accordance with Card Network dispute resolution guidelines.',
        },
        contact: {
          heading: '7. Billing & Grievance Contact Details',
          intro: 'To request a refund, submit proof of transaction, or clarify billing queries, please contact our Grievance & Billing Officer:',
          pocLabel: 'Point of Contact / Grievance Officer:',
          pocValue: 'Dibyendu Kumar Khaskel',
          emailLabel: 'Support & Billing Email:',
          emailValue: 'support@daycal.com',
          phoneLabel: 'Helpline / Phone:',
          phoneValue: '+91 6294629735',
          phoneSuffix: ' (10:00 AM - 7:00 PM IST)',
          helpdeskLabel: 'Official Helpdesk:',
          helpdeskValue: 'DayCal Contact Us Portal',
          addressLabel: 'Operating Address:',
          addressValue: 'Helencha Road, Duttapulia, Baikola, West Bengal 743270, India',
          slaLabel: 'Grievance Resolution SLA:',
          slaValue: 'Initial response within 24 hours; final resolution within 3 business days.',
        },
      },
    },
    bn: {
      title: 'বাতিলকরণ ও রিফান্ড পলিসি',
      subtitle: 'DayCal সাবস্ক্রিপশন এবং পেইড অ্যাপয়েন্টমেন্ট শিডিউলিংয়ের জন্য স্বচ্ছ, নিরপেক্ষ এবং কমপ্লায়েন্ট রিফান্ড নীতিমালা।',
      badge: 'পেমেন্ট গেটওয়ে কমপ্লায়েন্স ভেরিফায়েড',
      lastUpdated: '২১ আগস্ট, ২০২৬',
      toc: [
        { id: 'overview', title: '১. পলিসির ভূমিকা ও পরিধি' },
        { id: 'subscription-refunds', title: '২. DayCal SaaS সাবস্ক্রিপশন রিফান্ড' },
        { id: 'booking-cancellations', title: '৩. অ্যাপয়েন্টমেন্ট বুকিং বাতিলকরণ' },
        { id: 'eligibility', title: '৪. রিফান্ডের যোগ্যতা ও ব্যতিক্রম' },
        { id: 'timelines', title: '৫. রিফান্ডের মাধ্যম ও সময়সীমা (৫–৭ দিন)' },
        { id: 'disputes', title: '৬. চার্জব্যাক ও বিরোধ নিষ্পত্তি' },
        { id: 'contact', title: '৭. বিলিং ও অভিযোগ যোগাযোগ' },
      ],
      facts: {
        card1: { label: 'Turnaround Time', value: '5 – 7 Business Days', sub: 'Credited to original source' },
        card2: { label: 'Guarantee', value: '14-Day SaaS Money-Back', sub: 'For first-time plan upgrades' },
        card3: { label: 'Refund Method', value: 'Original Payment Mode', sub: 'UPI, Card, or Net Banking' },
        card4: { label: 'Billing Support', value: 'billing@daycal.com', sub: '24–48h SLA response time' },
      },
      sidebar: {
        indexTitle: 'Policy Contents',
        helpText: 'Need immediate refund or billing assistance?',
        helpLink: 'Contact Billing Team',
      },
      sections: {
        overview: {
          heading: '1. Overview & Policy Scope',
          p1: 'This Cancellation & Refund Policy sets out the terms under which refunds and cancellations are processed for services provided by <strong>DayCal Platform Inc.</strong> ("DayCal", "we", "our", or "us").',
          p2: 'DayCal operates in two primary payment contexts:',
          boxATitle: 'A. Platform SaaS Plans',
          boxABody: 'Subscriptions paid directly to DayCal by hosts for software tools, advanced calendar sync, custom domains, and agency team access.',
          boxBTitle: 'B. Paid Appointment Bookings',
          boxBBody: 'Consultation fees paid by attendees to hosts via integrated payment gateways (Razorpay, Stripe) for booking time slots.',
        },
        subscriptionRefunds: {
          heading: '2. DayCal SaaS Subscriptions',
          block1Title: '14-Day Money-Back Guarantee',
          block1Body: 'If you upgrade to a paid DayCal plan (Pro, Agency, or Enterprise) for the first time and find that the platform does not meet your scheduling needs, you may request a <strong>100% full refund within 14 days</strong> of your initial purchase date. No questions asked.',
          block2Title: 'Self-Serve Subscription Cancellation',
          block2Body: 'You can cancel your recurring DayCal subscription at any time with a single click from your <strong>Dashboard &gt; Settings &gt; Billing</strong>. Upon cancellation:',
          block2Items: [
            'Your account remains active with full premium benefits until the conclusion of the currently paid billing period.',
            'No further automatic renewals or billing charges will occur after the expiration date.',
            'Your bookings, custom links, and settings will gracefully downgrade to the Free Tier without data loss.',
          ],
          block3Title: 'Duplicate or Erroneous Charges',
          block3Body: 'In the event of accidental duplicate payments, currency conversion errors, or billing system anomalies, our automated detection or manual support will issue an immediate 100% reversal.',
        },
        bookingCancellations: {
          heading: '3. Host Appointment Cancellations & Rescheduling',
          intro: 'When attendees book paid appointments with DayCal hosts, the following cancellation and refund policies apply:',
          card1Title: 'Advance Notice Cancellations (24+ Hours)',
          card1Body: 'Attendees who cancel a scheduled appointment at least <strong>24 hours prior</strong> to the scheduled meeting start time are eligible for a <strong>100% full refund</strong> or a free reschedule to another available host slot.',
          card2Title: 'Host-Initiated Cancellations',
          card2Body: 'If a host cancels an appointment or is unable to attend a scheduled session, the attendee is guaranteed an <strong>automatic 100% full refund</strong> or prioritized rescheduling.',
          card3Title: 'Rescheduling Flexibility',
          card3Body: 'Attendees can reschedule their booking at zero penalty directly through their booking confirmation link up until the host\'s configured reschedule deadline.',
        },
        eligibility: {
          heading: '4. Refund Eligibility & Non-Refundable Scenarios',
          eligibleTitle: 'Eligible for Full Refund',
          eligibleItems: [
            '• First-time subscription refund requests within 14 days.',
            '• Technical failure on DayCal preventing video link generation or calendar sync.',
            '• Duplicate payment charges caused by network drops.',
            '• Appointments cancelled by host or with >=24h attendee notice.',
          ],
          nonRefundableTitle: 'Non-Refundable Scenarios',
          nonRefundableItems: [
            '• Client no-shows without any prior cancellation notice.',
            '• Successfully completed consultations and meetings.',
            '• Subscription renewal requests made after the 14-day window.',
            '• Consumed third-party add-ons or custom domain setup fees.',
          ],
        },
        timelines: {
          heading: '5. Mode of Refund & Turnaround Timelines',
          subheading: 'Official Payment Gateway Settlement SLA',
          intro: 'To comply with standard international payment gateway regulations (including RBI guidelines, Visa/Mastercard rules, Razorpay, and Stripe):',
          items: [
            '<strong class="text-slate-900">Mode of Refund:</strong> All approved refunds are credited <strong>strictly back to the original payment source</strong> (Credit Card, Debit Card, UPI ID, Net Banking account, or Digital Wallet) used to make the payment. No cash refunds or untraceable third-party transfers are permitted.',
            '<strong class="text-slate-900">Processing SLA:</strong> DayCal initiates approved refund requests within <strong>24 to 48 business hours</strong>.',
            '<strong class="text-slate-900">Bank Settlement Timeline:</strong> Once initiated, the refunded funds typically reflect in the customer\'s bank account or card balance within <strong>5 to 7 business days</strong> (depending on the issuing bank\'s clearing cycle).',
            '<strong class="text-slate-900">Refund Reference Number (ARN):</strong> You will receive an automated email receipt containing the unique Gateway Refund Reference / Acquirer Reference Number (ARN) for bank tracking.',
          ],
        },
        disputes: {
          heading: '6. Chargebacks & Dispute Resolution',
          p1: 'We strongly encourage customers to contact DayCal Billing Support prior to initiating a bank chargeback or payment gateway dispute. Our dedicated support desk resolves 99% of billing inquiries within <strong>24 to 48 hours</strong>.',
          p2: 'If a chargeback is initiated, DayCal provides full transaction audit logs, booking confirmation emails, and delivery status to the payment processor in accordance with Card Network dispute resolution guidelines.',
        },
        contact: {
          heading: '7. Billing & Grievance Contact Details',
          intro: 'To request a refund, submit proof of transaction, or clarify billing queries, please contact our Grievance & Billing Officer:',
          pocLabel: 'Point of Contact / Grievance Officer:',
          pocValue: 'Dibyendu Kumar Khaskel',
          emailLabel: 'Support & Billing Email:',
          emailValue: 'support@daycal.com',
          phoneLabel: 'Helpline / Phone:',
          phoneValue: '+91 6294629735',
          phoneSuffix: ' (10:00 AM - 7:00 PM IST)',
          helpdeskLabel: 'Official Helpdesk:',
          helpdeskValue: 'DayCal Contact Us Portal',
          addressLabel: 'Operating Address:',
          addressValue: 'Helencha Road, Duttapulia, Baikola, West Bengal 743270, India',
          slaLabel: 'Grievance Resolution SLA:',
          slaValue: 'Initial response within 24 hours; final resolution within 3 business days.',
        },
      },
    },
  },

  contact: {
    en: {
      title: 'Contact Us & Official Support',
      subtitle: 'Get in touch directly with our support team, grievance officer, or billing desk.',
      badge: 'Direct Official Support Active',
      directContactTitle: 'Direct Point of Contact',
      officerSubtext: 'DayCal Platform Administration & Support',
      officerTitle: 'Point of Contact / Grievance Officer',
      officerName: 'Dibyendu Kumar Khaskel',
      emailTitle: 'Email Support',
      emailValue: 'support@daycal.com',
      phoneTitle: 'Direct Helpline / Phone',
      phoneValue: '+91 6294629735',
      hoursTitle: 'Operating Hours',
      hoursValue: 'Monday – Saturday: 10:00 AM – 7:00 PM IST',
      slaTitle: 'Response Turnaround (SLA)',
      slaValue: 'Initial response within 24 hours. Priority queue for Pro & Agency hosts.',
      addressTitle: 'Official Operating Address',
      addressValue: 'Helencha Road, Duttapulia, Baikola, West Bengal 743270, India',
      googleTrustTitle: 'Google API Verification Contact',
      googleTrustDesc: 'For Google Cloud OAuth application verification auditors or security inquiries, please email support@daycal.com with subject line:',
      googleTrustBadge: 'Google API Verification',
      billingTitle: 'Billing & Gateway Disputes',
      billingDesc: 'For queries regarding payments, subscriptions, or refund status, please refer to our dedicated policy or email our team.',
      refundBtn: 'View Refund Policy',
      quickLinksTitle: 'Legal & Compliance Documents',
      linkPrivacy: 'Privacy Policy',
      linkTerms: 'Terms of Service Agreement',
      linkRefund: 'Cancellation & Refund Policy',
      faqTitle: 'Frequently Asked Questions',
      faqs: [
        {
          q: 'How does DayCal prevent double bookings?',
          a: 'DayCal builds your open slots from your weekly working hours, buffer times, and existing DayCal bookings — so clients can only book times you are actually free.',
        },
        {
          q: 'Can I embed my booking page on my website?',
          a: 'Yes, you can easily embed your DayCal booking page onto your website using our inline embed code or by sharing a direct link.',
        },
        {
          q: 'Is it possible to collect payments during booking?',
          a: 'Absolutely. DayCal integrates with popular payment gateways so you can collect fees or deposits at the time of booking.',
        },
        {
          q: 'Is my calendar data secure and private?',
          a: 'Yes. We use secure OAuth connections and only check for free/busy times to prevent double-booking. Your private event details remain secure.',
        },
        {
          q: 'Do you support automatic timezone detection?',
          a: 'Yes, our system automatically detects your invitee\'s timezone, ensuring they see your available slots in their local time.',
        },
      ],
    },
    bn: {
      title: 'যোগাযোগ ও অফিসিয়াল সাপোর্ট',
      subtitle: 'আমাদের সাপোর্ট টিম, অভিযোগ কর্মকর্তা বা বিলিং ডেস্কের সাথে সরাসরি যোগাযোগ করুন।',
      badge: 'অফিসিয়াল সাপোর্ট সক্রিয়',
      directContactTitle: 'Direct Point of Contact',
      officerSubtext: 'DayCal Platform Administration & Support',
      officerTitle: 'যোগাযোগের ব্যক্তি / অভিযোগ কর্মকর্তা',
      officerName: 'দিব্যেন্দু কুমার খাসকেল',
      emailTitle: 'ইমেইল সাপোর্ট',
      emailValue: 'support@daycal.com',
      phoneTitle: 'সরাসরি হেল্পলাইন / ফোন',
      phoneValue: '+91 6294629735',
      hoursTitle: 'অফিস সময়',
      hoursValue: 'সোমবার – শনিবার: সকাল ১০:০০ – সন্ধ্যা ৭:০০ (IST)',
      slaTitle: 'রেসপন্স সময়সীমা (SLA)',
      slaValue: '২৪ ঘণ্টার মধ্যে প্রাথমিক সমাধান। প্রো ও এজেন্সি হোস্টদের জন্য অগ্রাধিকার।',
      addressTitle: 'অফিসিয়াল ঠিকানা',
      addressValue: 'হেলেনচা রোড, দত্তপুলিয়া, বাইকোলা, পশ্চিমবঙ্গ ৭৪৩২৭০, ভারত',
      googleTrustTitle: 'গুগল এপিআই ভেরিফিকেশন যোগাযোগ',
      googleTrustDesc: 'গুগল ক্লাউড ও-অথ অ্যাপ ভেরিফিকেশন অডিটর বা সিকিউরিটি পর্যালোচনার জন্য support@daycal.com-এ যোগাযোগ করুন সাবজেক্ট সহ:',
      googleTrustBadge: 'Google API Verification',
      billingTitle: 'বিলিং ও পেমেন্ট বিরোধ',
      billingDesc: 'পেমেন্ট, সাবস্ক্রিপশন বা রিফান্ডের বিবরণের জন্য আমাদের রিফান্ড পলিসি দেখুন বা সরাসরি ইমেইল করুন।',
      refundBtn: 'রিফান্ড পলিসি দেখুন',
      quickLinksTitle: 'আইনি ও কমপ্লায়েন্স নথি',
      linkPrivacy: 'Privacy Policy',
      linkTerms: 'Terms of Service Agreement',
      linkRefund: 'Cancellation & Refund Policy',
      faqTitle: 'সচরাচর জিজ্ঞাসিত প্রশ্ন (FAQ)',
      faqs: [
        {
          q: 'DayCal কীভাবে ডাবল বুকিং প্রতিরোধ করে?',
          a: 'DayCal আপনার সাপ্তাহিক কাজের সময়, বাফার সময় এবং বিদ্যমান DayCal বুকিং থেকে খালি স্লট তৈরি করে — তাই ক্লায়েন্টরা শুধুমাত্র আপনার ফাঁকা সময়েই বুক করতে পারে।',
        },
        {
          q: 'আমি কি আমার ওয়েবসাইটে আমার বুকিং পেজ এম্বেড করতে পারি?',
          a: 'হ্যাঁ, আপনি আমাদের ইনলাইন এম্বেড কোড ব্যবহার করে বা সরাসরি লিঙ্ক শেয়ার করে সহজেই আপনার ওয়েবসাইটে আপনার DayCal বুকিং পেজ এম্বেড করতে পারেন।',
        },
        {
          q: 'বুকিং করার সময় পেমেন্ট সংগ্রহ করা কি সম্ভব?',
          a: 'অবশ্যই। DayCal জনপ্রিয় পেমেন্ট গেটওয়ের সাথে একত্রিত হয় যাতে আপনি বুকিংয়ের সময় ফি বা ডিপোজিট সংগ্রহ করতে পারেন।',
        },
        {
          q: 'আমার ক্যালেন্ডারের ডেটা কি সুরক্ষিত এবং ব্যক্তিগত?',
          a: 'হ্যাঁ। আমরা সুরক্ষিত ও-অথ (OAuth) সংযোগ ব্যবহার করি এবং ডাবল-বুকিং রোধ করতে শুধুমাত্র ফ্রি/ব্যস্ত সময় চেক করি। আপনার ব্যক্তিগত ইভেন্টের বিবরণ সুরক্ষিত থাকে।',
        },
        {
          q: 'আপনারা কি স্বয়ংক্রিয় টাইমজোন সনাক্তকরণ সমর্থন করেন?',
          a: 'হ্যাঁ, আমাদের সিস্টেম স্বয়ংক্রিয়ভাবে আপনার আমন্ত্রিতের টাইমজোন সনাক্ত করে, নিশ্চিত করে যে তারা তাদের স্থানীয় সময়ে আপনার উপলব্ধ স্লটগুলি দেখতে পায়।',
        },
      ],
    },
  },
};

// Admin sidebar / editor listing (order + display labels for the four managed pages).
export const LEGAL_PAGES = [
  { slug: 'privacy', label: 'Privacy Policy', path: '/privacy' },
  { slug: 'terms', label: 'Terms of Service', path: '/terms' },
  { slug: 'refund', label: 'Cancellation & Refund', path: '/refund' },
  { slug: 'contact', label: 'Contact Us', path: '/contact' },
];

// Deep-merge an override onto a default. Objects merge key-by-key; strings and arrays are
// replaced wholesale when the override provides them (the admin edits whole TOC/FAQ arrays).
export function mergeCopy(base, over) {
  if (over === undefined || over === null) return base;
  if (base === undefined || base === null) return over;
  if (Array.isArray(base) || typeof base !== 'object' || typeof over !== 'object') return over;
  const out = { ...base };
  for (const k of Object.keys(over)) out[k] = mergeCopy(base[k], over[k]);
  return out;
}

// Nicer labels for the admin editor; falls back to a humanized key.
const FIELD_LABELS = {
  title: 'Title',
  subtitle: 'Subtitle',
  badge: 'Badge',
  lastUpdated: 'Last updated',
  toc: 'Table of contents',
  faqs: 'FAQs',
  q: 'Question',
  a: 'Answer',
  officerName: 'Officer name',
  emailValue: 'Email',
  phoneValue: 'Phone',
  hoursValue: 'Operating hours',
  slaValue: 'Response SLA',
  addressValue: 'Address',
  googleTrustDesc: 'Google verification description',
  billingDesc: 'Billing description',
  refundBtn: 'Refund button label',
};

export function humanizeKey(key) {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

// --- Public-page hook: fetch overrides once (module-cached) and merge over defaults ---
let overridesCache = null;
let overridesPromise = null;

function loadOverrides() {
  if (overridesCache) return Promise.resolve(overridesCache);
  if (!overridesPromise) {
    overridesPromise = api
      .getLegalPages()
      .then((r) => {
        overridesCache = r?.pages || {};
        return overridesCache;
      })
      .catch(() => ({})); // network/API failure → fall back to built-in defaults
  }
  return overridesPromise;
}

/** Returns the merged copy (defaults + admin overrides) for one page + locale. */
export function useLegalCopy(slug, locale) {
  const defaults = LEGAL_DEFAULTS[slug] || {};
  const [overrides, setOverrides] = useState(overridesCache);

  useEffect(() => {
    let alive = true;
    loadOverrides().then((o) => {
      if (alive) setOverrides(o);
    });
    return () => {
      alive = false;
    };
  }, []);

  const pageOver = overrides?.[slug] || {};
  const merged = {
    en: mergeCopy(defaults.en, pageOver.en),
    bn: mergeCopy(defaults.bn, pageOver.bn),
  };
  return merged[locale] || merged.en;
}
